import React, { Fragment, PureComponent } from 'react';
import { connect } from 'react-redux';
import Dropzone from 'react-dropzone';
import { Collapse, Dialog, LinearProgress } from '@mui/material';
import { CloudDownload } from '@mui/icons-material';
import pLimit from 'p-limit';
import uuid from 'uuid';
import { IDispatchProps, IProps, IStateProps } from './interfaces';
import {
  IVaultFile,
  resetVaultUploadDialog,
  setFileUploadStatus,
  setIsUploading,
  toggleVaultUploadDialog,
  updateDescriptionField, updateFileProgress,
  updateVaultUploadFiles,
  VaultUploadStatus,
} from '../../../../state/vaultUploadDialog';
import { IAppState } from '../../../../state/reducers';
import {
  ActionContainer,
  DialogActionsOverride,
  DialogContentContainer,
  DialogHeader,
  DialogHeaderTitle,
} from '../../../Shared/styles/dialog';
import Button from '../../../Shared/VaultButton';
import ProgressBar from '../../../Shared/ProgressBar/component';
import {
  DropContainer,
  DropNotice,
  FormRow,
  Label,
  SelectedFileContainer,
  SelectedFilesContainer,
  SelectedFilesText,
  StyledTextField,
  UploadStatusBar,
  UploadStatusText,
} from './styles';
import { upload, UploadToastType } from '../../../../utils/upload';
import { selectIsUserVaultSuperAdmin, selectUserOrgIds } from '../../../../state/user/userOrgRole';
import { Product } from '../../../../state/product';
import { ProgressContainer } from '../../../Exchange/GameScheduleForm/styles';
import { selectOptionsByName, LookupName } from '../../../../state/lookups';
import toast, { superAdminToast } from '../../../../utils/toast';

interface UploadToastEntry {
  toasts: Array<UploadToastType>;
  finishedToast: UploadToastType;
  fileName: string;
}

class VaultUploadDialog extends PureComponent<IProps> {
  onClose = () => {
    const { resetDialog } = this.props;
    resetDialog();
  };

  onCreateAsset = async () => {
    const {
      userOrgs, selectedFiles, vaultAssetMetaDetails, systemSettings, setIsUploadingFiles, setUploadStatus, updateUploadStageProgress, isVaultSuperAdmin, iptcCategoryOptions,
    } = this.props;

    // Add asset details as meta
    vaultAssetMetaDetails.description = vaultAssetMetaDetails.description || '';

    setIsUploadingFiles(true);

    const uploadToasts: Array<UploadToastEntry> = [];

    let toastIndex = 0;
    const totalUploads = selectedFiles.length;
    const limit = pLimit(3);
    const input: any = [];
    if (totalUploads > 1) superAdminToast(isVaultSuperAdmin, `Pre-Processing ${totalUploads} files`, { type: toast.TYPE.INFO });
    selectedFiles.forEach((selectedFile: IVaultFile) => {
      setUploadStatus(selectedFile.uploadTrackingId, VaultUploadStatus.NotStarted);
      input.push(limit(async () => {
        const curIndex = toastIndex;
        toastIndex += 1;
        uploadToasts.push({
          toasts: [],
          finishedToast: null,
          fileName: selectedFile.uploadFile.name,
        });
        setUploadStatus(selectedFile.uploadTrackingId, VaultUploadStatus.Initializing);
        await upload(
          selectedFile.uploadFile,
          userOrgs[0],
          Product.Vault,
          systemSettings,
          vaultAssetMetaDetails,
          {
            status: (uploadStatus: VaultUploadStatus) => {
              setUploadStatus(selectedFile.uploadTrackingId, uploadStatus);
            },
            progress: (progressPercentage: string) => {
              updateUploadStageProgress(selectedFile.uploadTrackingId, progressPercentage);
            },
            toast: !(totalUploads > 1) ? null : (toastType: UploadToastType) => {
              uploadToasts[curIndex].toasts.push(toastType);
              if (toastType === UploadToastType.transcodeTimeout) {
                // Always show
                uploadToasts[curIndex].finishedToast = toastType;
                superAdminToast(isVaultSuperAdmin, `Asset Creation Timed Out:\n ${uploadToasts[curIndex].fileName}`, { type: toast.TYPE.ERROR });
              } else if (toastType === UploadToastType.created) {
                if (!uploadToasts[curIndex].finishedToast) uploadToasts[curIndex].finishedToast = toastType;
              } else if (toastType === UploadToastType.uploadError) {
                // Always show
                superAdminToast(isVaultSuperAdmin, `Error uploading: ${uploadToasts[curIndex].fileName}`, { type: toast.TYPE.ERROR });
                uploadToasts[curIndex].finishedToast = toastType;
              }
            },
          },
          isVaultSuperAdmin,
          iptcCategoryOptions,
        );
      }));
    });

    await Promise.all(input);

    if (totalUploads > 1) {
      const failedTranscodes = uploadToasts.filter((entry) => entry.finishedToast === UploadToastType.transcodeTimeout).map((entry) => entry.fileName).join(', ');
      const failedUploads = uploadToasts.filter((entry) => entry.finishedToast === UploadToastType.uploadError).map((entry) => entry.fileName).join(', ');
      const completedUploads = uploadToasts.filter((entry) => entry.finishedToast === UploadToastType.created);

      // Single toast for all completed
      if (completedUploads.length > 0) {
        superAdminToast(isVaultSuperAdmin, `Successfully Uploaded ${completedUploads.length} Files`, { type: toast.TYPE.SUCCESS });
      }

      // Single toast for all failed
      let errors = '';
      if (failedTranscodes.length > 0) errors += `Failed Transcodes: ${failedTranscodes}`;
      if (failedUploads.length > 0) {
        if (errors.length > 0) errors += '\n';
        errors += `Failed Uploads: ${failedUploads}`;
      }
      if (errors.length > 0) superAdminToast(isVaultSuperAdmin, errors, { type: toast.TYPE.ERROR });
    }
    setIsUploadingFiles(false, true);
  };

  onFilesDropped = (files: Array<File>) => {
    const { updateFiles } = this.props;

    const vaultUploadedFiles: Array<IVaultFile> = [];
    files.forEach((file: File) => {
      vaultUploadedFiles.push({
        uploadFile: file,
        uploadTrackingId: uuid.v4(),
        uploadStatus: VaultUploadStatus.NotStarted,
      });
    });
    updateFiles(vaultUploadedFiles);
  };

  getUploadStatusText = (uploadStatus: VaultUploadStatus, progressPercentage?: string): string => {
    switch (uploadStatus) {
      case VaultUploadStatus.PreProcessing:
        return `Step 1 of 2: Scanning - ${progressPercentage || '0'}%`;
      case VaultUploadStatus.Uploading:
        return `Step 2 of 2: Uploading - ${progressPercentage || '0'}%`;
      case VaultUploadStatus.CheckTranscode:
        return 'Checking for Transcode';
      case VaultUploadStatus.Initializing:
        return 'Initializing';
      case VaultUploadStatus.NotStarted:
        return 'Not Started';
      case VaultUploadStatus.Complete:
        return 'Completed';
      case VaultUploadStatus.Error:
        return 'Error occurred';
      default:
        return '';
    }
  };

  render() {
    const {
      isOpen,
      isUploading,
      selectedFiles,
      onUpdateDescriptionField,
      vaultAssetMetaDetails,
      isUploadComplete,
      onClose,
    } = this.props;

    const hasFiles = (selectedFiles && selectedFiles.length > 0);

    const { description } = vaultAssetMetaDetails || {};

    return (
      <Dialog open={isOpen} fullWidth maxWidth="md" onClose={onClose}>
        <>
          <DialogHeader>
            <DialogHeaderTitle data-auto="VaultUploadModal">
              Upload Vault Asset
            </DialogHeaderTitle>
          </DialogHeader>
          <ProgressContainer />
          <DialogContentContainer>
            <Collapse in={!hasFiles}>
              <FormRow>
                <Dropzone onDrop={this.onFilesDropped}>
                  {({ getRootProps, getInputProps }) => (
                    <DropContainer {...getRootProps()}>
                      <input {...getInputProps()} />
                      <DropNotice>
                        <CloudDownload /> Drop Files Here
                      </DropNotice>
                    </DropContainer>
                  )}
                </Dropzone>
              </FormRow>
            </Collapse>
            {hasFiles && (
              <FormRow>
                <Label>{`${selectedFiles.length} File${selectedFiles.length > 1 ? 's' : ''} to be Uploaded`}</Label>
                <SelectedFilesContainer>
                  {selectedFiles.map((vaultFile: IVaultFile) => (
                    <SelectedFileContainer key={vaultFile.uploadTrackingId}>
                      <SelectedFilesText>{vaultFile.uploadFile.name}</SelectedFilesText>
                      <UploadStatusBar>
                        <ProgressBar stage={vaultFile.uploadStatus} percent={vaultFile.progressPercentage} />
                      </UploadStatusBar>
                      <UploadStatusText>
                        {`${this.getUploadStatusText(vaultFile.uploadStatus, vaultFile.progressPercentage)}`}
                      </UploadStatusText>
                    </SelectedFileContainer>
                  ))}
                </SelectedFilesContainer>
              </FormRow>
            )}
            <FormRow>
              <Label>Description</Label>
              <StyledTextField
                onChange={(e) => onUpdateDescriptionField(e.target.value)}
                value={description || ''}
                disabled={!hasFiles || isUploading}
                fullWidth
                placeholder="Enter Description"
              />
            </FormRow>
          </DialogContentContainer>
          <DialogActionsOverride disableSpacing>
            <ActionContainer>
              <Button
                variant="plainOnPaper"
                onClick={this.onClose}
                data-auto="vaultUploadCancelBtn"
                disabled={isUploading}
              >
                Cancel
              </Button>
              <Button
                variant="primaryOnWhite"
                onClick={isUploadComplete ? this.onClose : this.onCreateAsset}
                disabled={!hasFiles || isUploading}
                data-auto="vaultUploadCreateBtn"
              >
                {isUploadComplete ? 'Done' : 'Create'}
              </Button>
            </ActionContainer>
          </DialogActionsOverride>
        </>
      </Dialog>
    );
  }
}

const mapStateToProps = (state: IAppState): IStateProps => ({
  isOpen: state.vaultUploadDialog.isOpen,
  isUploading: state.vaultUploadDialog.isUploading,
  isUploadComplete: state.vaultUploadDialog.isUploadComplete,
  selectedFiles: state.vaultUploadDialog.selectedFiles,
  vaultAssetMetaDetails: state.vaultUploadDialog.vaultAssetMetaDetails,
  userOrgs: selectUserOrgIds(state),
  systemSettings: state.systemSettings.systemSettings,
  isVaultSuperAdmin: selectIsUserVaultSuperAdmin(state),
  iptcCategoryOptions: selectOptionsByName(LookupName.iptcCategory, state),
});

const mapDispatchToProps: IDispatchProps = {
  toggleDialog: toggleVaultUploadDialog,
  resetDialog: resetVaultUploadDialog,
  updateFiles: updateVaultUploadFiles,
  onUpdateDescriptionField: updateDescriptionField,
  setIsUploadingFiles: setIsUploading,
  setUploadStatus: setFileUploadStatus,
  updateUploadStageProgress: updateFileProgress,
  onClose: resetVaultUploadDialog,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(VaultUploadDialog);
